export enum Direction {
  LEFT = -1,
  CENTER = 0,
  RIGHT = 1,
}

export enum EventType {
  SHOW = 1,
  RESIZE = 2,
  EDIT = 3,
  SELECT = 4,
  NOTE = 5,
  FLASHCARD_DECK = 6,
  VIEWPORT = 7,
}

export enum LogLevel {
  DEBUG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
  DISABLE = 9,
}

export interface NodeData {
  'leading-line-color'?: string;
  'leading-line-width'?: number;
  'leading-line-style'?: FlashMapLineStyle;
  'leading-line-type'?: FlashMapLineType;
  'background-color'?: string;
  'foreground-color'?: string;
  'font-size'?: number;
  'font-weight'?: string;
  'font-style'?: string;
  'background-image'?: string;
  image?: NodeImageMeta;
  flashcardsCount?: number;
  hasFlashcards?: boolean;
  note?: NoteMeta;
  data?: FlashcardMeta;
  width?: number;
  height?: number;
  'background-rotation'?: number;
  [key: string]: unknown;
}

export type FlashMapLineStyle = 'solid' | 'dashed' | 'dotted';
export type FlashMapLineType = 'straight' | 'dynamic';

export interface FlashMapNodeLineStyle {
  color?: string;
  width?: number;
  style?: FlashMapLineStyle;
  type?: FlashMapLineType;
}

export interface NodeImageMeta {
  src: string;
  workspaceAssetId?: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
  width?: number;
  height?: number;
}

export interface FlashcardMeta {
  flashcardsCount?: number;
  hasFlashcards?: boolean;
  note?: NoteMeta;
  image?: NodeImageMeta;
  data?: FlashcardMeta;
  [key: string]: unknown;
}

export interface NoteMeta {
  id?: number | string;
  href?: string;
  text?: string;
  [key: string]: unknown;
}

export interface FlashMapNodeMetricTooltip {
  totalCards: number;
  studiedCards: number;
  matureCards: number;
  masteryText?: string;
  studiedText?: string;
  ariaLabel?: string;
}

export interface FlashMapRootMetricChartItem {
  label: string;
  percentage: number;
}

export interface FlashMapRootMetricChart {
  items: readonly FlashMapRootMetricChartItem[];
  ariaLabel?: string;
}

export interface FlashMapNodeMetric {
  text: string;
  dueText?: string;
  tooltip?: FlashMapNodeMetricTooltip | null;
  rootChart?: FlashMapRootMetricChart | null;
}

/**
 * A string remains supported for backwards compatibility with existing consumers.
 */
export type FlashMapNodeMetricValue = string | FlashMapNodeMetric | null | undefined;

export type FlashMapNodeMetricsMap = Record<string, FlashMapNodeMetricValue>;

export type FlashMapAppearanceTheme = 'light' | 'dark';

export interface FlashMapDrawingOptions {
  enabled?: boolean;
}

export interface FlashMapNode {
  id: string;
  index: number;
  topic: string;
  data: NodeData;
  isroot: boolean;
  parent: FlashMapNode | null;
  direction: Direction;
  expanded: boolean;
  children: FlashMapNode[];
  _data: {
    view?: {
      abs_x: number;
      abs_y: number;
      width: number;
      height: number;
    };
  };
}

export interface MindMapMeta {
  name: string;
  author: string;
  version: string;
}

export interface MindMapData {
  meta?: {
    name?: string;
    author?: string;
    version?: string;
  };
  format: 'node_tree' | 'node_array' | 'freemind';
  data: unknown;
  drawings?: FlashMapDrawingDocument;
}

export type FlashMapDrawingTool = 'pen' | 'rectangle' | 'ellipse' | 'arrow';
export type FlashMapDrawingLineStyle = 'solid' | 'dashed';
export type FlashMapDrawingNodeLayer = 'above-nodes' | 'below-nodes';

export interface FlashMapDrawingPoint {
  x: number;
  y: number;
}

export interface FlashMapDrawingStroke {
  id: number;
  tool: FlashMapDrawingTool;
  color: string;
  lineWidth: number;
  /** Defaults to solid when omitted by documents saved before this setting existed. */
  lineStyle?: FlashMapDrawingLineStyle;
  /** Defaults to above-nodes when omitted by documents saved before this setting existed. */
  nodeLayer?: FlashMapDrawingNodeLayer;
  fill?: string | null;
  rotation?: number;
  points: FlashMapDrawingPoint[];
}

export interface FlashMapDrawingDocument {
  version: 1;
  strokes: FlashMapDrawingStroke[];
}

export interface FlashMapOptions {
  container?: string | HTMLElement;
  editable?: boolean;
  theme?: string | null;
  enableOutlineMode?: boolean;
  assetBasePath?: string;
  mode?: 'full' | 'side';
  layout_mode?: FlashMapLayoutMode;
  support_html?: boolean;
  drawing?: FlashMapDrawingOptions;
  log_level?: 'debug' | 'info' | 'warn' | 'error' | 'disable';
  view?: {
    engine?: 'canvas' | 'svg';
    enable_device_pixel_ratio?: boolean;
    hmargin?: number;
    vmargin?: number;
    line_width?: number;
    line_color?: string;
    line_style?: 'curved' | 'straight';
    custom_line_render?: (arg: {
      ctx: CanvasRenderingContext2D | SVGPathElement;
      start_point: { x: number; y: number };
      end_point: { x: number; y: number };
    }) => void;
    draggable?: boolean;
    hide_scrollbars_when_draggable?: boolean;
    node_overflow?: 'hidden' | 'wrap' | 'visible';
    zoom?: { min?: number; max?: number; step?: number; mask_key?: number };
    custom_node_render?:
      | ((jm: unknown, ele: HTMLElement, node: FlashMapNode) => boolean | void)
      | null;
    expander_style?: 'char' | 'number';
    pinch_sensitivity?: number;
    wheel_sensitivity?: number;
    style_toolbar?: Record<string, string>;
  };
  layout?: {
    hspace?: number;
    mindmap_hspace?: number;
    vspace?: number;
    pspace?: number;
    cousin_space?: number;
  };
  default_event_handle?: {
    enable_mousedown_handle?: boolean;
    enable_click_handle?: boolean;
    enable_dblclick_handle?: boolean;
    enable_mousewheel_handle?: boolean;
  };
  shortcut?: {
    enable?: boolean;
    handles?: Record<string, (e: KeyboardEvent) => void>;
    mapping?: Record<string, number | number[]>;
    id_generator?: () => string;
  };
  plugin?: Record<string, object>;
}

export type FlashMapLayoutMode = 'mindmap' | 'org-chart' | 'list';

export interface EventData {
  evt?: string;
  data?: unknown[];
  node?: string;
  nodes?: string[];
}

export type FlashMapEventCallback = (type: EventType, data: EventData) => void;

export interface FlashMapPresentationState {
  active: boolean;
  currentIndex: number;
  total: number;
  currentNodeId: string | null;
}
